import React, { useRef, useState } from 'react';
import {
    STAGES,
    dealTypeLabel,
    dealValue,
    followUpState,
    formatEurShort,
    isColdSource,
    probabilityOf,
    stageClass,
} from '../utils/salesPipeline';
import '../styles/KanbanBoard.css';

const PRIORITY_COLORS = { High: 'prio-high', Medium: 'prio-medium', Low: 'prio-low' };

const FOLLOW_UP_LABEL = {
    overdue: 'überfällig',
    today: 'heute',
    missing: 'ohne Wiedervorlage',
};

const initials = (name) => (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');

function FollowUpBadge({ acq }) {
    const state = followUpState(acq);
    if (state === 'none') return null;
    if (state === 'upcoming') {
        return (
            <span className="kanban-followup is-upcoming" title="Nächste Wiedervorlage">
                {new Date(acq.nextFollowUpDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
            </span>
        );
    }
    return (
        <span className={`kanban-followup is-${state}`} title="Wiedervorlage">
            {FOLLOW_UP_LABEL[state]}
        </span>
    );
}

function KanbanCard({ acq, onDragStart, onCardClick }) {
    const probability = probabilityOf(acq);
    return (
        <div
            className="kanban-card"
            draggable
            onDragStart={(e) => onDragStart(e, acq.id)}
            onClick={() => onCardClick(acq)}
        >
            <div className="kanban-card-header">
                <span className="kanban-card-title">{acq.projectTitle}</span>
                <span className={`kanban-prio ${PRIORITY_COLORS[acq.priority] || 'prio-medium'}`}>
                    {acq.priority}
                </span>
            </div>
            <div className="kanban-card-company">{acq.companyName}</div>

            <div className="kanban-card-row">
                <span className={`kanban-type type-${acq.dealType || 'project'}`}>
                    {dealTypeLabel(acq.dealType)}
                </span>
                <span className="kanban-card-budget">{formatEurShort(dealValue(acq))}</span>
            </div>

            <div className="kanban-prob" title={`Gewinnwahrscheinlichkeit ${probability} %`}>
                <span className="kanban-prob-bar">
                    <span className="kanban-prob-fill" style={{ width: `${probability}%` }} />
                </span>
                <span className="kanban-prob-value">{probability} %</span>
            </div>

            <div className="kanban-card-footer">
                <span className="kanban-card-contact">
                    {acq.ownerName && <span className="kanban-owner" title={`Verantwortlich: ${acq.ownerName}`}>{initials(acq.ownerName)}</span>}
                    {acq.contactPerson}
                </span>
                <span className="kanban-card-meta">
                    {isColdSource(acq.leadSource) && acq.contactAttempts > 0 && (
                        <span className="kanban-attempts" title={`${acq.contactAttempts} Kontaktversuche`}>
                            ☎ {acq.contactAttempts}
                        </span>
                    )}
                    <FollowUpBadge acq={acq} />
                </span>
            </div>
        </div>
    );
}

export default function KanbanBoard({ acquisitions, onStatusChange, onCardClick }) {
    const [dragOverStage, setDragOverStage] = useState(null);
    const dragId = useRef(null);

    const handleDragStart = (e, id) => {
        dragId.current = id;
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, stageKey) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverStage(stageKey);
    };

    const handleDrop = (e, stageKey) => {
        e.preventDefault();
        setDragOverStage(null);
        if (dragId.current) {
            onStatusChange(dragId.current, stageKey);
            dragId.current = null;
        }
    };

    const handleDragLeave = () => setDragOverStage(null);

    return (
        <div className="kanban-board">
            {STAGES.map((stage) => {
                const cards = acquisitions.filter((a) => a.status === stage.key);
                const isOver = dragOverStage === stage.key;
                const columnValue = cards.reduce((sum, a) => sum + dealValue(a), 0);
                return (
                    <div
                        key={stage.key}
                        className={`kanban-column ${isOver ? 'kanban-column-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, stage.key)}
                        onDrop={(e) => handleDrop(e, stage.key)}
                        onDragLeave={handleDragLeave}
                    >
                        <div className={`kanban-column-header ${stageClass(stage.key)}`} title={stage.hint}>
                            <div className="kanban-column-headline">
                                <span className="kanban-column-label">{stage.label}</span>
                                <span className="kanban-column-count">{cards.length}</span>
                            </div>
                            <span className="kanban-column-value">{formatEurShort(columnValue)}</span>
                        </div>
                        <div className="kanban-column-body">
                            {cards.map((acq) => (
                                <KanbanCard
                                    key={acq.id}
                                    acq={acq}
                                    onDragStart={handleDragStart}
                                    onCardClick={onCardClick}
                                />
                            ))}
                            {cards.length === 0 && (
                                <div className="kanban-empty-drop">Hierher ziehen</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
